/**
 * index.jsx
 *
 * @Author: jruif
 * @Date: 2017/7/1 下午2:14
 */

import React, { isValidElement, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Rolling from '../icons/internals/Rolling';
import chainPropTypes from './chainPropTypes';

const colors = ['primary', 'secondary', 'success', 'warning', 'info', 'error'];
/**
 * Button
 * */
class Button extends React.PureComponent {
  static defaultProps = {
    loading: false,
    disabled: false,
    variant: 'flat',
    size: 'medium',
    prefixCls: 'ne-button',
    type: 'button',
    color: 'secondary',
    shape: 'default',
    checked: false,
    component: 'button',
    tabIndex: '0',
    children: chainPropTypes(PropTypes.node, props => {
      const found = React.Children.toArray(props.children).some(
        child => React.isValidElement(child) && child.props.onClick,
      );

      if (found) {
        return new Error(
          [
            'NeatUI: 你正试图给button的子元素添加点击事件回调。',
            'Firefox 将不会触发该事件',
            'https://github.com/mui-org/material-ui/issues/13957',
          ].join('\n'),
        );
      }

      return null;
    }),
  };

  static propTypes = {
    /** @ignore */
    children: PropTypes.node,
    /**
     * 设置按钮类型
     * */
    variant: PropTypes.oneOf(['contained', 'flat', 'text', 'outlined', 'hollow']),
    /**
     * 设置按钮颜色
     * */
    color: PropTypes.oneOf(colors),
    /**
     * 图形按钮
     * */
    prefix: PropTypes.node,
    /**
     * 类前缀
     * */
    prefixCls: PropTypes.string,
    className: PropTypes.string,
    /**
     * 设置按钮形状，可选值为 circle 或者不设
     * circle: 圆形
     * dashed: 虚线
     * borderless: 虚线
     * default: 默认
     * */
    shape: PropTypes.oneOf(['circle', 'dash', 'default', 'none', 'borderless']),
    /**
     * 尺寸
     * */
    size: PropTypes.oneOf(['large', 'medium', 'small']),
    /**
     * 按钮类型
     * */
    type: PropTypes.oneOf(['submit', 'button', 'reset']),
    /**
     * 加载中
     * */
    loading: PropTypes.bool,
    /**
     * 是否禁用
     * */
    disabled: PropTypes.bool,
    /**
     * @ignore
     * */
    active: PropTypes.bool,
    /**
     * 节点元素
     * */
    component: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  };

  constructor(props) {
    super(props);
    this.state = {
      clicked: false,
    };
  }

  handleClick = e => {
    const { disabled, onClick } = this.props;
    if (!disabled) {
      this.setState({ clicked: true });
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.setState({ clicked: false }), 300);

      if (onClick) {
        onClick(e);
      }
    }
  };

  // Handle auto focus when click button in Chrome
  handleMouseUp = e => {
    const { onMouseUp } = this.props;
    if (onMouseUp) {
      onMouseUp(e);
    }
  };

  render() {
    const {
      type,
      variant,
      shape,
      size,
      className,
      htmlType,
      children,
      icon,
      prefixCls,
      hollow,
      loading,
      prefix,
      color,
      active,
      component,
      disabled,
      tabIndex,
      ...other
    } = this.props;

    const { clicked } = this.state;
    const buttonVariant = type === 'primary' ? 'contained' : hollow ? 'hollow' : variant; // 兼容
    const buttonColor = colors.includes(type) ? type : color; // 兼容
    const hasIcon = loading ? 'loading' : icon || prefix;
    const classes = classNames(
      prefixCls,
      `${prefixCls}-color-${buttonColor}`,
      `${prefixCls}-shape-${shape === 'none' ? 'default' : shape}`,
      `${prefixCls}-${buttonVariant}`,
      {
        [`${prefixCls}-${size}`]: size,
        [`${prefixCls}-loading`]: loading,
        [`${prefixCls}-clicked`]: clicked,
        [`${prefixCls}-active`]: active,
        [`${prefixCls}-disabled`]: disabled,
      },
      className,
    );

    let ComponentProp = component;

    if (ComponentProp === 'button' && other.href) {
      ComponentProp = 'a';
    }

    const buttonProps = {};
    if (ComponentProp === 'button') {
      buttonProps.type = htmlType || type || 'button';
      buttonProps.disabled = disabled || loading;
    } else {
      buttonProps.role = 'button';
    }
    const prefixIcon =
      prefix && isValidElement(prefix)
        ? cloneElement(prefix, {
            className: classNames(prefix.props.className, `${prefixCls}-prefix`),
          })
        : null;
    return (
      <ComponentProp
        {...buttonProps}
        {...other}
        className={classes}
        onMouseUp={this.handleMouseUp}
        tabIndex={disabled ? '-1' : tabIndex}
        onClick={this.handleClick}>
        {loading ? (
          <Rolling className={`${prefixCls}-loading-icon ${prefixCls}-prefix`} />
        ) : (
          prefixIcon
        )}
        {children}
      </ComponentProp>
    );
  }
}

export default Button;



// WEBPACK FOOTER //
// ./src/button/Button.jsx